import { Component, DestroyRef, OnDestroy, effect, inject, signal } from '@angular/core';
import { LucideAngularModule, RefreshCw } from 'lucide-angular';
import { fromEvent, Subscription } from 'rxjs';

import { AppUpdateService } from '../../../core/app-update.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-update-available-modal',
  standalone: true,
  imports: [LucideAngularModule],
  templateUrl: './update-available-modal.component.html',
  styleUrl: './update-available-modal.component.scss'
})
export class UpdateAvailableModalComponent implements OnDestroy {
  private readonly appUpdate = inject(AppUpdateService);
  private readonly destroyRef = inject(DestroyRef);
  private keySub?: Subscription;

  readonly icons = { refresh: RefreshCw };
  readonly showModal = this.appUpdate.showUpdateModal;
  readonly currentVersion = environment.version;

  /** Live highlight for Shift + Ctrl + R hard-reload teaching. */
  readonly shiftPressed = signal(false);
  readonly ctrlPressed = signal(false);
  readonly rPressed = signal(false);

  constructor() {
    effect(() => {
      if (this.showModal()) {
        this.bindShortcutKeys();
      } else {
        this.unbindShortcutKeys();
      }
    });

    this.destroyRef.onDestroy(() => this.unbindShortcutKeys());
  }

  ngOnDestroy(): void {
    this.unbindShortcutKeys();
  }

  reload(): void {
    this.appUpdate.resolveUpdateModal(true);
  }

  dismiss(): void {
    this.appUpdate.resolveUpdateModal(false);
  }

  private bindShortcutKeys(): void {
    this.unbindShortcutKeys();
    this.resetKeyHighlights();

    this.keySub = new Subscription();
    this.keySub.add(
      fromEvent<KeyboardEvent>(window, 'keydown').subscribe((event) => {
        this.applyKeyState(event, true);
      })
    );
    this.keySub.add(
      fromEvent<KeyboardEvent>(window, 'keyup').subscribe((event) => {
        this.applyKeyState(event, false);
      })
    );
    this.keySub.add(fromEvent(window, 'blur').subscribe(() => this.resetKeyHighlights()));
  }

  private unbindShortcutKeys(): void {
    this.keySub?.unsubscribe();
    this.keySub = undefined;
    this.resetKeyHighlights();
  }

  private resetKeyHighlights(): void {
    this.shiftPressed.set(false);
    this.ctrlPressed.set(false);
    this.rPressed.set(false);
  }

  private applyKeyState(event: KeyboardEvent, down: boolean): void {
    const key = event.key;
    if (key === 'Shift') {
      this.shiftPressed.set(down);
      return;
    }
    if (key === 'Control' || key === 'Meta') {
      this.ctrlPressed.set(down);
      return;
    }
    if (key === 'r' || key === 'R') {
      this.rPressed.set(down);
    }
  }
}
